import flatpickr from "flatpickr";
import { useState, useEffect } from "react";
import { X, Video, MapPin, Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import { sessionService } from "../../../application/SessionApplicationService";
import { listTherapistPatients, listTherapistCouplePatients } from "../../../application/sessionPatients";
import DatePicker from "../../../../../shared/ui/form/date-picker";
import Input from "../../../../../shared/ui/form/input/InputField";
import Label from "../../../../../shared/ui/form/Label";
import Button from "../../../../../shared/ui/button/Button";
import { Session } from "../../../domain/Session";
import FilterSelect from "../../../../../shared/ui/form/FilterSelect";
import PillSelector from "../../../../../shared/ui/form/PillSelector";
import { THERAPY_TYPES } from "../../../../../shared/domain/therapyTypes";
import { useCourseLabels, useSessionPreferences } from "../../../../center";


interface EditSessionModalProps {
    isOpen: boolean;
    onClose: () => void;
    session: Session;
    onSuccess: () => void;
}

export default function EditSessionModal({
    isOpen,
    onClose,
    session,
    onSuccess,
}: EditSessionModalProps) {
    const { t } = useTranslation();
    // A centre may leave the clinical fields out; what a session already has is kept.
    const { therapyFieldsEnabled } = useSessionPreferences();
    // A training centre's student is named with the course they are on.
    const { labelOf } = useCourseLabels();
    const [loading, setLoading] = useState(false);
    const [patients, setPatients] = useState<{ value: string; label: string }[]>([]);
    const [patientsLoading, setPatientsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [formData, setFormData] = useState({
        title: "",
        date: "",
        patient_id: "",
        couple_patient_id: "",
        therapy_type: "",
        hour: "",
        ubication: "", // 'online' | 'in_person'
        type: "", // 'individual' | 'group'
        duration: 0,
        template_id: null as string | null,
    });

    useEffect(() => {
        if (session) {
            setFormData({
                title: session.title || "",
                date: session.date || "",
                patient_id: session.patient_id || "",
                couple_patient_id: session.couple_patient_id || "",
                therapy_type: session.therapy_type || "",
                hour: session.hour || "",
                ubication: session.ubication || "",
                type: session.type || "",
                duration: session.duration || 0,
                template_id: session.template_id || null,
            });
        }
    }, [session]);

    useEffect(() => {
        const fetchPatients = async () => {
            if (!session.therapist_id) return;
            try {
                setPatientsLoading(true);

                if (session.type === 'couple') {
                    const couples = await listTherapistCouplePatients(session.therapist_id);
                    const data = couples || [];
                    setPatients(data.map((p: { id: string, display_name: string }) => ({
                        value: p.id,
                        label: p.display_name || "Couple Patient",
                    })));
                } else {
                    const response = await listTherapistPatients(session.therapist_id);
                    const data = response.patients || [];
                    setPatients(data.map((p: { id: string, first_name: string, last_name?: string, course_id?: string | null }) => ({
                        value: p.id,
                        label: labelOf(p),
                    })));
                }
            } catch (error) {
                console.error("Error fetching patients:", error);
            } finally {
                setPatientsLoading(false);
            }
        };

        fetchPatients();
    }, [session.therapist_id, session.type, labelOf]);

    const handleChange = (newValues: Partial<typeof formData>) => {
        setFormData(prev => ({ ...prev, ...newValues }));
    };

    const handleSubmit = async () => {
        setLoading(true);
        setError(null);
        try {
            // Clean up data before sending to backend
            const payload = {
                ...formData,
                patient_id: formData.patient_id === "" ? null : formData.patient_id,
                couple_patient_id: formData.couple_patient_id === "" ? null : formData.couple_patient_id,
                template_id: formData.template_id || null,
                ubication: formData.ubication || null,
                type: formData.type || null,
            };

            await sessionService.updateSession(session.id, payload as Partial<Session>);
            onSuccess();
            onClose();
        } catch (err: unknown) {
            console.error(err);
            setError(t("session.saveError", "Error saving session. Please try again."));
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    // Parse therapy types safely handling spaces
    const therapyTypes = formData.therapy_type
        ? formData.therapy_type.split(",").map(t => t.trim()).filter(Boolean)
        : [];

    const therapyOptions = THERAPY_TYPES.map((type) => ({
        value: type,
        label: t(`session.therapyOptions.${type}`),
    }));

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
            {/* Overlay */}
            <div
                className="absolute inset-0 bg-black/40 backdrop-blur-sm"
                onClick={onClose}
            />

            {/* Modal */}
            <div className="relative w-full max-w-2xl rounded-2xl border border-gray-200 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-900 max-h-[90vh] overflow-y-auto flex flex-col">
                {/* Header */}
                <div className="flex justify-between items-center p-6 border-b border-gray-100 bg-gray-50/50 sticky top-0 z-10 backdrop-blur-md">
                    <h2 className="text-xl font-bold text-content-primary">
                        {t("session.editSessionDetails")}
                    </h2>
                    <button
                        onClick={onClose}
                        className="p-2 rounded-full hover:bg-gray-200 transition-colors text-content-tertiary hover:text-gray-600"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-6 space-y-8">
                    {/* Section 1: Who & What */}
                    <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Patient Search */}
                            <div>
                                <Label className="mb-2 uppercase text-xs font-semibold text-content-tertiary tracking-wider">{t("session.patient")}</Label>
                                <FilterSelect
                                    options={patients}
                                    value={session.type === 'couple' ? formData.couple_patient_id : formData.patient_id}
                                    onChange={(val) => {
                                        if (session.type === 'couple') {
                                            handleChange({ couple_patient_id: val as string })
                                        } else {
                                            handleChange({ patient_id: val as string })
                                        }
                                    }}
                                    placeholder={patientsLoading ? t("common.loading") : t("session.selectPatient")}
                                    searchPlaceholder={t("patient.searchPlaceholder")}
                                    className="w-full"
                                />
                            </div>

                            {/* Session Title */}
                            <div>
                                <Label htmlFor="title" className="mb-2 uppercase text-xs font-semibold text-content-tertiary tracking-wider">{t("session.title")}</Label>
                                <Input
                                    type="text"
                                    id="title"
                                    value={formData.title}
                                    placeholder={t("session.titlePlaceholder")}
                                    className="rounded-xl bg-gray-50 border-gray-200 focus:bg-white transition-all py-3"
                                    onChange={(e) => handleChange({ title: e.target.value })}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Section 2: When (Date & Time) */}
                    <div>
                        <Label className="mb-3 uppercase text-xs font-semibold text-content-tertiary tracking-wider">{t("session.dateTime")}</Label>
                        <div className="p-4 bg-gray-50 rounded-xl border border-gray-100 grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Date Picker wrapper */}
                            <div className="bg-white p-1 rounded-lg shadow-sm border border-gray-100">
                                <DatePicker
                                    id="date-picker-modal"
                                    label=""
                                    placeholder={t("session.selectDate")}
                                    defaultDate={formData.date}
                                    onChange={(selectedDates) => {
                                        const formattedDate = selectedDates[0]
                                            ? flatpickr.formatDate(selectedDates[0], "Y-m-d")
                                            : "";
                                        handleChange({ date: formattedDate });
                                    }}
                                    className="border-none shadow-none focus-within:ring-0"
                                />
                            </div>

                            {/* Time Input wrapper */}
                            <div className="bg-white p-1 rounded-lg shadow-sm border border-gray-100">
                                <div className="relative">
                                    <DatePicker
                                        id="time-picker-modal"
                                        mode="time"
                                        placeholder={t("session.time")}
                                        defaultDate={formData.hour}
                                        onChange={(_, currentTimeString) => {
                                            handleChange({ hour: currentTimeString });
                                        }}
                                        className="border-none shadow-none focus-within:ring-0"
                                        calendarClassName="time-picker-lg"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Section 3: Duration, Location & Type */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {/* Session Type - Fixed to individual or couple based on original session */}
                        <div>
                            <Label className="mb-2 text-xs font-semibold text-content-tertiary">{t("session.sessionType")}</Label>
                            <div className="w-full px-4 py-3 border border-gray-200 rounded-xl leading-5 bg-gray-50 text-gray-500 font-medium cursor-not-allowed text-sm">
                                {session.type === 'couple' ? t("session.typeOptions.couple") : t("session.typeOptions.individual")}
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {/* Location */}
                        <div>
                            <Label className="mb-3 uppercase text-xs font-semibold text-content-tertiary tracking-wider">{t("session.location")}</Label>
                            <div className="flex bg-gray-100 p-1 rounded-xl">
                                <button
                                    type="button"
                                    onClick={() => handleChange({ ubication: 'online' })}
                                    className={`flex-1 flex items-center justify-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${formData.ubication === 'online' ? 'bg-white text-accent-text shadow-sm' : 'text-gray-500 hover:text-gray-900'}`}
                                >
                                    <Video className="w-4 h-4" />
                                    <span>{t("session.locationOptions.online")}</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleChange({ ubication: 'in person' })}
                                    className={`flex-1 flex items-center justify-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${formData.ubication === 'in person' ? 'bg-white text-accent-text shadow-sm' : 'text-gray-500 hover:text-gray-900'}`}
                                >
                                    <MapPin className="w-4 h-4" />
                                    <span>{t("session.locationOptions.in_person")}</span>
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {/* Duration */}
                        <div>
                            <Label className="mb-3 uppercase text-xs font-semibold text-content-tertiary tracking-wider">{t("session.duration")}</Label>
                            <PillSelector
                                options={[
                                    { value: 30, label: '30m' },
                                    { value: 50, label: '50m' },
                                    { value: 60, label: '60m' },
                                    { value: 90, label: '90m' },
                                ]}
                                value={formData.duration}
                                onChange={(val) => handleChange({ duration: Number(val) })}
                            />
                        </div>

                        {/* Therapy Type */}
                        {therapyFieldsEnabled && (
                        <div>
                            <Label className="mb-2 uppercase text-xs font-semibold text-content-tertiary tracking-wider">{t("session.therapyType")} ({t("common.optional")})</Label>
                            <FilterSelect
                                options={therapyOptions}
                                value={therapyTypes}
                                onChange={(val) => {
                                    const arr = Array.isArray(val) ? val : [val];
                                    handleChange({ therapy_type: arr.join(", ") });
                                }}
                                placeholder={t("session.therapyType")}
                                searchPlaceholder={t("common.search")}
                                icon={<Search className="w-5 h-5" />}
                                multiple={true}
                                allowCustomValues={true}
                                addCustomLabel={t("common.add")}
                            />
                        </div>
                        )}
                    </div>

                    {error && <p className="text-red-500 text-sm text-center bg-red-50 p-2 rounded-lg">{error}</p>}
                </div>

                <div className="border-t border-gray-100 p-6 pb-10 sm:pb-6 flex justify-end gap-3 bg-gray-50/50 sticky bottom-0 backdrop-blur-md rounded-b-2xl">
                    <Button variant="outline" onClick={onClose} disabled={loading} className="rounded-xl px-6">
                        {t("common.cancel")}
                    </Button>
                    <Button onClick={handleSubmit} disabled={loading} className="rounded-xl px-8 ">
                        {loading ? t("common.loading") : t("common.save")}
                    </Button>
                </div>
            </div>
        </div>
    );
}
